import { GameplayUI } from './ui/GameplayUI';
import EventBus from './EventBus';
import MobileControls, { isTouchDevice } from './ui/MobileControls';

type OnSkinChange = (skin: string) => void;
type OnNameChange = (name: string) => void;

type InputState = {
  moveDirection: number;
  rotateDirection: number;
  mouseVelocityX: number;
  fire: boolean;
  turbo: boolean;
  shield: boolean;
};

class InputHandler {
  rotationSensitivity: number;
  canvas: HTMLCanvasElement;
  state: InputState;
  onSkinChange: OnSkinChange;
  onNameChange: OnNameChange;
  envManager: any;
  private mobileControls?: MobileControls;
  private isMobileDevice: boolean;
  private mobileTurretValue: number;
  private mobileTurretLoopHandle: number | null;
  private mobileTurretSensitivity: number;

  // Stable handler references to properly add/remove listeners
  private handleKeyDownRef: (event: KeyboardEvent) => void;
  private handleKeyUpRef: (event: KeyboardEvent) => void;
  private handleMouseDownRef: (event: MouseEvent) => void;
  private handleMouseUpRef: (event: MouseEvent) => void;
  private handleMouseMoveRef: (event: MouseEvent) => void;

  constructor(onSkinChange: OnSkinChange, onNameChange: OnNameChange, envManager: any = null) {
    this.rotationSensitivity = -0.2;
    this.canvas = document.getElementsByTagName('canvas')[0] as HTMLCanvasElement;

    this.state = {
      moveDirection: 0,
      rotateDirection: 0,
      mouseVelocityX: 0,
      fire: false,
      turbo: false,
      shield: false,
    };

    this.onSkinChange = onSkinChange;
    this.onNameChange = onNameChange;
    this.envManager = envManager;
    this.isMobileDevice = isTouchDevice();
    this.mobileTurretValue = 0;
    this.mobileTurretLoopHandle = null;
    this.mobileTurretSensitivity = 3.5;

    // Bind stable handler references once
    this.handleKeyDownRef = (e: KeyboardEvent) => this.handleKeyDown(e);
    this.handleKeyUpRef = (e: KeyboardEvent) => this.handleKeyUp(e);
    this.handleMouseDownRef = (e: MouseEvent) => this.handleMouseDown(e);
    this.handleMouseUpRef = (e: MouseEvent) => this.handleMouseUp(e);
    this.handleMouseMoveRef = (e: MouseEvent) => this.handleMouseMove(e);
  }

  initInputHandling() {
    window.addEventListener('keydown', this.handleKeyDownRef);
    window.addEventListener('keyup', this.handleKeyUpRef);
    window.addEventListener('mousedown', this.handleMouseDownRef);
    window.addEventListener('mouseup', this.handleMouseUpRef);
    window.addEventListener('mousemove', this.handleMouseMoveRef);

    if (this.isMobileDevice) {
      this.setupMobileControls();
    }
  }

  removeInputHandling() {
    window.removeEventListener('keydown', this.handleKeyDownRef);
    window.removeEventListener('keyup', this.handleKeyUpRef);
    window.removeEventListener('mousedown', this.handleMouseDownRef);
    window.removeEventListener('mouseup', this.handleMouseUpRef);
    window.removeEventListener('mousemove', this.handleMouseMoveRef);

    if (this.isMobileDevice) {
      this.teardownMobileControls();
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    switch (event.key.toLowerCase()) {
      // Movement
      case 'w':
        this.state.moveDirection = 1;
        break;
      case 's':
        this.state.moveDirection = -1;
        break;
      case 'a':
        this.state.rotateDirection = 1;
        break;
      case 'd':
        this.state.rotateDirection = -1;
        break;

      // Pause menu
      case 'p':
        GameplayUI.showPauseMenu(
          this.onSkinChange,
          this.onNameChange,
          GameplayUI.getCookie('tankSkin'),
          GameplayUI.getCookie('playerName')
        );
        break;
      case 'escape':
        GameplayUI.hidePauseMenu();
        break;

      // Actions
      case ' ':
        this.state.fire = true;
        break;
      case 'shift':
        this.state.turbo = true;
        break;
      case 'c':
        this.state.shield = true;
        break;

      // Envs (use function keys to avoid weapon key conflicts)
      case 'f1':
        if (this.envManager) {
          this.envManager.switchEnvironment('garage');
        }
        break;
      case 'f2':
        if (this.envManager) {
          this.envManager.switchEnvironment('game');
        }
        break;

      // For testing
      case 'k':
        EventBus.emit('player:kill');
        break;
    }
  }

  handleKeyUp(event: KeyboardEvent) {
    if (GameplayUI.isPauseMenuVisible()) return;
    switch (event.key.toLowerCase()) {
      case 'w':
      case 's':
        this.state.moveDirection = 0;
        break;
      case 'a':
      case 'd':
        this.state.rotateDirection = 0;
        break;
      case ' ':
        this.state.fire = false;
        break;
      case 'shift':
        this.state.turbo = false;
        break;
      case 'c':
        this.state.shield = false;
        break;
    }
  }

  handleMouseMove(event: MouseEvent) {
    if (GameplayUI.isPauseMenuVisible()) return;
    if (document.pointerLockElement === this.canvas) {
      this.state.mouseVelocityX = event.movementX * this.rotationSensitivity;
    }
  }

  handleMouseDown(event: MouseEvent) {
    if (GameplayUI.isPauseMenuVisible()) return;
    if (event.button === 0) {
      this.state.fire = true;
    }
  }

  handleMouseUp(event: MouseEvent) {
    if (GameplayUI.isPauseMenuVisible()) return;
    if (event.button === 0) {
      this.state.fire = false;
    }
  }

  private setupMobileControls() {
    this.mobileControls = new MobileControls({
      onMove: (forward, rotate) => {
        this.state.moveDirection = forward;
        this.state.rotateDirection = rotate;
      },
      onTurretChange: (value) => {
        this.mobileTurretValue = value;
      },
      onFireStart: () => {
        this.state.fire = true;
      },
      onFireEnd: () => {
        this.state.fire = false;
      },
    });
    this.mobileControls.attach();
    this.startMobileTurretLoop();
  }

  private teardownMobileControls() {
    if (this.mobileControls) {
      this.mobileControls.detach();
      this.mobileControls = undefined;
    }
    if (this.mobileTurretLoopHandle !== null) {
      cancelAnimationFrame(this.mobileTurretLoopHandle);
      this.mobileTurretLoopHandle = null;
    }
    this.mobileTurretValue = 0;
  }

  private startMobileTurretLoop() {
    const loop = () => {
      if (Math.abs(this.mobileTurretValue) > 0.05) {
        this.state.mouseVelocityX = this.mobileTurretValue * this.mobileTurretSensitivity;
      }
      this.mobileTurretLoopHandle = requestAnimationFrame(loop);
    };
    this.mobileTurretLoopHandle = requestAnimationFrame(loop);
  }

}

export default InputHandler;
